import { NAMED_COLORS, type NamedColor } from './types';

/**
 * Minecraft's chat colours, and the colour arithmetic the tellraw composer
 * needs on top of them.
 *
 * Two jobs need a sense of "how different do these colours look": stepping a
 * hex colour or a gradient down to the sixteen named colours when 1.15 and
 * older is switched on (those versions have no hex), and spreading a gradient
 * across a word. Both happen in OKLab rather than in sRGB, because OKLab
 * distances track what people see. In sRGB a gold-to-red gradient dips through
 * a muddy middle and #ff8800 measures closer to red than to gold; in OKLab the
 * steps look even and the nearest named colour is the one a player would pick.
 *
 * Everything here is pure and deterministic, so the chat preview, the
 * compiled command and the tests agree on every colour.
 */

/** The vanilla palette, exactly as the game renders the sixteen named colours. */
export const MC_COLORS: Record<NamedColor, string> = {
  black: '#000000',
  dark_blue: '#0000aa',
  dark_green: '#00aa00',
  dark_aqua: '#00aaaa',
  dark_red: '#aa0000',
  dark_purple: '#aa00aa',
  gold: '#ffaa00',
  gray: '#aaaaaa',
  dark_gray: '#555555',
  blue: '#5555ff',
  green: '#55ff55',
  aqua: '#55ffff',
  red: '#ff5555',
  light_purple: '#ff55ff',
  yellow: '#ffff55',
  white: '#ffffff',
};

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** One of the sixteen names, spelled exactly as the game spells it (lower-case). */
export function isNamedColor(value: unknown): value is NamedColor {
  return typeof value === 'string' && (NAMED_COLORS as readonly string[]).includes(value);
}

/**
 * `#rrggbb` in either case. Deliberately not a type guard: narrowing a string
 * to `string` would leave `never` in the else branch.
 */
export function isHexColor(value: unknown): boolean {
  return typeof value === 'string' && HEX_PATTERN.test(value);
}

/** The one spelling of a hex colour the composer stores and emits. */
export function normalizeHex(value: string): string {
  return value.toLowerCase();
}

/** Any colour the composer accepts as `#rrggbb`, or `fallback` when it is neither a name nor hex. */
export function resolveColor(color: string | null | undefined, fallback = '#ffffff'): string {
  if (isNamedColor(color)) return MC_COLORS[color];
  if (typeof color === 'string' && isHexColor(color)) return normalizeHex(color);
  return fallback;
}

function channels(hex: string): [number, number, number] {
  const value = parseInt(resolveColor(hex).slice(1), 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function toHex(rgb: readonly number[]): string {
  return `#${rgb.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
const clampUnit = (c: number) => (Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0);

/** A colour in OKLab as `[L, a, b]` (Björn Ottosson's matrices). Unreadable colours count as white. */
export function toOklab(hex: string): [number, number, number] {
  const [r, g, b] = channels(hex).map(channel => toLinear(channel / 255));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** An OKLab colour back to `#rrggbb`, each channel clamped into sRGB. */
export function fromOklab(lab: readonly [number, number, number]): string {
  const [L, a, b] = lab;
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  return toHex(linear.map(c => Math.round(toGamma(clampUnit(c)) * 255)));
}

let paletteLab: [NamedColor, [number, number, number]][] | null = null;

/** Below this OKLab chroma a colour reads as grey, whatever its nominal hue. */
const ACHROMATIC_CHROMA = 0.03;
const GREYS: ReadonlySet<NamedColor> = new Set(['black', 'dark_gray', 'gray', 'white']);

/**
 * The named colour that looks closest (smallest OKLab distance). Ties go to
 * the earlier colour code, so the answer never depends on iteration order.
 *
 * A grey only ever steps to one of the four greys. Plain distance would send
 * a mid grey such as #808080 to dark_aqua, whose lightness happens to be
 * closer, and a grey sentence turning teal on old servers is exactly the kind
 * of surprise the compatibility notes exist to prevent.
 */
export function nearestNamedColor(hex: string): NamedColor {
  if (isNamedColor(hex)) return hex;
  paletteLab ??= NAMED_COLORS.map(name => [name, toOklab(MC_COLORS[name])]);
  const [L, a, b] = toOklab(hex);
  const grey = Math.hypot(a, b) < ACHROMATIC_CHROMA;
  let best: NamedColor = 'white';
  let bestDistance = Infinity;
  for (const [name, [pL, pa, pb]] of paletteLab) {
    if (grey && !GREYS.has(name)) continue;
    const distance = (L - pL) ** 2 + (a - pa) ** 2 + (b - pb) ** 2;
    if (distance < bestDistance) {
      best = name;
      bestDistance = distance;
    }
  }
  return best;
}

/** The drop shadow the game draws under chat text: each channel at a quarter, rounded down. */
export function shadowColor(hex: string): string {
  return toHex(channels(hex).map(channel => Math.floor(channel * 0.25)));
}
